// @ts-nocheck
import React, { useState } from "react";
import {
  Box, Chip, Divider, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Typography,
} from "@mui/material";
import useStyles from "../DaikinConsistency.style";
import { applySortOrder, SortableHeader, getFlaggedFields, UnitNameAddr, IssueDotHeaderCell, IssueDotCell } from "./shared";

// ConsistencySystemCard

const ConsistencySystemCard = ({ sys, outdoors, controls, services }) => {
  const classes = useStyles();
  const sysId = sys.sKey;

  const serviceMap = {};
  for (const [key, u] of services) serviceMap[key] = [key, u];

  const controlRows = controls.map(([cId, ctrl]) => {
    const linked = (ctrl.serviceUnits || [])
      .map(sid => serviceMap[sid] ? [sid, serviceMap[sid][1]] : null)
      .filter(Boolean);
    return { cId, ctrl, linked };
  });

  const linkedSvcIds = new Set(controlRows.flatMap(r => r.linked.map(([id]) => id)));
  const unlinkedServices = services.filter(([id]) => !linkedSvcIds.has(id));

  const cardClass = `${classes.systemCard} ${sys.isVirtual ? classes.systemCardVirtual : classes.systemCardDefault}`;

  return (
    <Paper elevation={0} className={cardClass}>
      <Box className={classes.systemHeaderBox}>
        <Box className={classes.systemHeaderFlex}>
          <Typography fontWeight={700} variant="body1">
            {sys.name || `System ${sys.internalId ?? sysId.slice(-6)}`}
          </Typography>
          {sys.isVirtual && <Chip label="virtual" color="warning" size="small" />}
          {sys.isConnected === false && <Chip label="disconnected" variant="outlined" size="small" />}
          {sys.internalId != null && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
              sysNum={sys.internalId}
            </Typography>
          )}
          {sys.issues?.length > 0 && (
            <Tooltip
              title={
                <Box>
                  {sys.issues.map((issue, i) => (
                    <Typography key={i} variant="caption" display="block" sx={{ color: 'text.primary', display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Box component="span" sx={{ color: issue.priority === 'high' ? '#f44336' : '#ffa726', fontSize: 10, lineHeight: 1 }}>●</Box>
                      {issue.description}
                    </Typography>
                  ))}
                </Box>
              }
              placement="top"
              arrow
              componentsProps={{ tooltip: { sx: { bgcolor: 'background.paper', color: 'text.primary', boxShadow: 3, maxWidth: 'unset', width: 'max-content', p: '20px' } }, arrow: { sx: { color: 'background.paper' } } }}
            >
              <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, cursor: 'help' }}>
                {sys.issues.filter(i => i.priority === "high").length > 0 && (
                  <Chip label={`${sys.issues.filter(i => i.priority === "high").length} high`} color="error" size="small" className={classes.chipMd} />
                )}
                {sys.issues.filter(i => i.priority !== "high").length > 0 && (
                  <Chip label={`${sys.issues.filter(i => i.priority !== "high").length} med`} color="warning" size="small" className={classes.chipMd} />
                )}
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
      <Divider />
      <Box className={classes.systemBodyBox}>
        {outdoors.length > 0 && (
          <ConsistencyOutdoorSection outdoors={outdoors} sysNum={sys.internalId} sysId={sys.systemId} />
        )}
        {(controls.length > 0 || unlinkedServices.length > 0) ? (
          <ConsistencyIndoorSection controlRows={controlRows} unlinkedServices={unlinkedServices} />
        ) : outdoors.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No indoor units assigned</Typography>
        ) : null}
      </Box>
    </Paper>
  );
};

// ConsistencyOutdoorSection

export const ConsistencyOutdoorSection = ({ outdoors, sysNum, sysId }) => {
  const classes = useStyles();
  const [sort, setSort] = useState({ col: null, dir: 'asc' });
  const handleSort = (col) =>
    setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const sorted = applySortOrder(outdoors, sort.col, sort.dir,
    ([, u], col) => col === 'airnet' ? u.airnet : col === 'systemNumber' ? u.systemNumber : col === 'systemId' ? u.systemId : null);

  return (
    <Box className={classes.outdoorTableBox}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" className={classes.sectionLabel}>
        Outdoor ({outdoors.length})
      </Typography>
      <TableContainer>
        <Table size="small" className={classes.tableAuto}>
          <TableHead>
            <TableRow>
              <IssueDotHeaderCell />
              <TableCell className={classes.th}>Outdoor</TableCell>
              <SortableHeader col="airnet" current={sort.col} dir={sort.dir} onSort={handleSort} className={classes.th}>Airnet</SortableHeader>
              <SortableHeader col="systemNumber" current={sort.col} dir={sort.dir} onSort={handleSort} className={classes.th}>Sys Num</SortableHeader>
              <SortableHeader col="systemId" current={sort.col} dir={sort.dir} onSort={handleSort} className={classes.th}>Sys ID</SortableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {(sysNum != null || sysId != null) && (
              <TableRow className={classes.rowSystemHeader}>
                <TableCell sx={{ width: 10, minWidth: 10, maxWidth: 10, p: 0 }} />
                <TableCell className={classes.tdSystemLabel} colSpan={2}>system</TableCell>
                <TableCell className={classes.tdSystemValue}>{sysNum ?? "—"}</TableCell>
                <TableCell className={classes.tdSystemValue}>{sysId != null ? String(sysId) : "—"}</TableCell>
              </TableRow>
            )}
            {sorted.map(([id, u]) => {
              const flagged = getFlaggedFields(u);
              return (
                <TableRow key={id} className={u.isConnected === false ? classes.rowDisconnected : undefined}>
                  <IssueDotCell issues={u.issues} />
                  <TableCell className={classes.td}>
                    <UnitNameAddr name={u.name} address={u.address} id={id} isVirtual={u.isVirtual} isConnected={u.isConnected} issues={u.issues} />
                  </TableCell>
                  <TableCell className={classes.tdMono} sx={flagged.has('airnet') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                    {u.airnet ?? "—"}
                  </TableCell>
                  <TableCell className={classes.tdMono} sx={flagged.has('systemNumber') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                    {u.systemNumber ?? "—"}
                  </TableCell>
                  <TableCell className={classes.tdMono} sx={flagged.has('systemId') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                    {u.systemId != null ? String(u.systemId) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// ConsistencyIndoorSection

export const ConsistencyIndoorSection = ({ controlRows, unlinkedServices }) => {
  const classes = useStyles();
  return (
    <Box>
      <TableContainer>
        <Table size="small" className={classes.tableAuto}>
          <TableHead>
            <TableRow>
              <IssueDotHeaderCell />
              <TableCell className={classes.th}>Control</TableCell>
              <TableCell className={classes.th}>Indoor ID</TableCell>
              <IssueDotHeaderCell divider />
              <TableCell className={classes.th}>Service</TableCell>
              <TableCell className={classes.th}>Indoor ID</TableCell>
              <TableCell className={classes.th}>MAC</TableCell>
              <TableCell className={classes.th}>Airnet</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {controlRows.map(({ cId, ctrl, linked }) => {
              const ctrlFlagged = getFlaggedFields(ctrl);
              return linked.length === 0 ? (
                <TableRow key={cId} className={ctrl.isConnected === false ? classes.rowDisconnected : undefined}>
                  <IssueDotCell issues={ctrl.issues} />
                  <TableCell className={classes.td}>
                    <UnitNameAddr name={ctrl.name} address={ctrl.address} id={cId} isVirtual={ctrl.isVirtual} isConnected={ctrl.isConnected} issues={ctrl.issues} />
                  </TableCell>
                  <TableCell className={classes.tdMono} sx={ctrlFlagged.has('indoorId') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                    {ctrl.indoorId ?? "—"}
                  </TableCell>
                  <IssueDotCell issues={[]} divider rowSpan={1} />
                  <TableCell className={classes.td} colSpan={4}>
                    <Typography variant="caption" color="text.secondary" fontStyle="italic">no services linked</Typography>
                  </TableCell>
                </TableRow>
              ) : linked.map(([sId, svc], i) => {
                const svcFlagged = getFlaggedFields(svc);
                const isRowDisconnected = ctrl.isConnected === false || svc.isConnected === false;
                return (
                  <TableRow key={`${cId}-${sId}`} className={isRowDisconnected ? classes.rowDisconnected : undefined}>
                    {i === 0 && (
                      <>
                        <IssueDotCell issues={ctrl.issues} rowSpan={linked.length} />
                        <TableCell rowSpan={linked.length} className={classes.td}>
                          <UnitNameAddr name={ctrl.name} address={ctrl.address} id={cId} isVirtual={ctrl.isVirtual} isConnected={ctrl.isConnected} issues={ctrl.issues} />
                        </TableCell>
                        <TableCell rowSpan={linked.length} className={classes.tdMono} sx={ctrlFlagged.has('indoorId') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                          {ctrl.indoorId ?? "—"}
                        </TableCell>
                      </>
                    )}
                    <IssueDotCell issues={svc.issues} divider />
                    <TableCell className={classes.td}>
                      <UnitNameAddr name={svc.name} address={svc.address} id={sId} isVirtual={svc.isVirtual} isConnected={svc.isConnected} issues={svc.issues} />
                    </TableCell>
                    <TableCell className={classes.tdMono} sx={svcFlagged.has('indoorId') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                      {svc.indoorId ?? "—"}
                    </TableCell>
                    <TableCell className={classes.tdMono} sx={svcFlagged.has('controlLink') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                      {svc.associatedControlAddress ?? "—"}
                    </TableCell>
                    <TableCell className={classes.tdMono} sx={svcFlagged.has('airnet') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                      {svc.airnet ?? "—"}
                    </TableCell>
                  </TableRow>
                );
              });
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {unlinkedServices.length > 0 && (
        <Box className={classes.unlinkedServicesBox}>
          <Typography variant="caption" fontWeight={700} color="warning.main" className={classes.unlinkedServicesLabel}>
            Services not linked to any control ({unlinkedServices.length})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <IssueDotHeaderCell />
                  <TableCell className={classes.th}>Service</TableCell>
                  <TableCell className={classes.th}>Indoor ID</TableCell>
                  <TableCell className={classes.th}>MAC</TableCell>
                  <TableCell className={classes.th}>Airnet</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unlinkedServices.map(([sId, svc]) => {
                  const svcFlagged = getFlaggedFields(svc);
                  return (
                    <TableRow key={sId} className={svc.isConnected === false ? classes.rowDisconnected : undefined}>
                      <IssueDotCell issues={svc.issues} />
                      <TableCell className={classes.td}>
                        <UnitNameAddr name={svc.name} address={svc.address} id={sId} isVirtual={svc.isVirtual} isConnected={svc.isConnected} issues={svc.issues} />
                      </TableCell>
                      <TableCell className={classes.tdMono} sx={svcFlagged.has('indoorId') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                        {svc.indoorId ?? "—"}
                      </TableCell>
                      <TableCell className={classes.tdMono} sx={svcFlagged.has('controlLink') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                        {svc.associatedControlAddress ?? "—"}
                      </TableCell>
                      <TableCell className={classes.tdMono} sx={svcFlagged.has('airnet') ? { color: 'error.main', fontWeight: 600 } : undefined}>
                        {svc.airnet ?? "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default ConsistencySystemCard;
